// src/pages/PurchasedPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ShoppingBag, ChevronRight, Plus } from 'lucide-react';

import apiClient from '../api/apiClient';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import { useAuthContext } from '../context/AuthContext';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';

const PurchasedPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, openAuthModal } = useAuthContext();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchPurchased = useCallback(async (url = null) => {
    if (!isLoggedIn) {
      setIsLoading(false);
      return;
    }
    try {
      if (!url) setIsLoading(true);
      else setIsFetchingMore(true);

      const finalUrl = url || '/products/user/purchased/';
      const response = await apiClient.get(finalUrl);
      
      // پاسخ به صورت صفحه‌بندی شده با کلیدهای results, next, count
      const results = response.data.results || response.data;
      const next = response.data.next || null;
      const count = response.data.count || results.length;

      setProducts(prev => url ? [...prev, ...results] : results);
      setNextPageUrl(next);
      setTotalCount(count);
      setIsError(false);
    } catch (error) {
      console.error('Error fetching purchased products:', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchPurchased();
  }, [fetchPurchased]);

  const handleLoadMore = () => {
    if (nextPageUrl && !isFetchingMore) {
      fetchPurchased(nextPageUrl);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <ShoppingBag size={48} className="text-gray-400 mb-4" />
        <p className="text-gray-700 mb-4">برای مشاهده محصولات خریداری شده، لطفاً وارد شوید.</p>
        <button onClick={openAuthModal} className="bg-purple-600 text-white px-6 py-2 rounded-lg">
          ورود / ثبت‌نام
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] rtl">
        <Loader2 className="w-10 h-10 animate-spin text-purple-700 mb-4" />
        <p className="text-gray-500 font-medium">در حال بارگذاری محصولات خریداری شده...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center min-h-[400px] rtl">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg text-center">
          خطا در دریافت محصولات خریداری شده. لطفاً دوباره تلاش کنید.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 rtl text-right font-vazir">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4 border-b pb-6">
        <div>
          <nav className="flex items-center text-xs text-gray-400 mb-2 font-medium">
            <Link to="/profile" className="hover:text-purple-600 transition">پروفایل کاربری</Link>
            <ChevronRight className="w-3 h-3 mx-1 rotate-180" />
            <span className="text-gray-600">محصولات خریداری شده</span>
          </nav>
          <h1 className="text-xs font-black text-gray-800 tracking-tight">خریدهای من</h1>
        </div>

        <div className="bg-purple-50 text-purple-700 border border-purple-100 px-5 py-2 rounded-2xl flex items-center shadow-sm">
          <span className="text-xl font-bold ml-2">{convertToPersianNumbers(totalCount)}</span>
          <span className="text-[11px] font-medium">محتوا را خریداری کرده‌اید</span>
        </div>
      </div>

      {/* Main Grid */}
      {products.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <div key={`${product.id}-${index}`} className="transition-all duration-300">
                <VerticalContentCard data={product} />
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {nextPageUrl && (
            <div className="flex justify-center mt-12 pb-10">
              <button
                onClick={handleLoadMore}
                disabled={isFetchingMore}
                className="flex items-center space-x-2 space-x-reverse bg-white border-2 border-purple-600 text-purple-600 px-8 py-3 rounded-xl font-bold hover:bg-purple-600 hover:text-white transition-all duration-300 disabled:opacity-50 shadow-lg shadow-purple-50"
              >
                {isFetchingMore ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>در حال بارگذاری...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>مشاهده موارد بیشتر</span>
                  </>
                )}
              </button>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-24 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-100">
          <ShoppingBag className="w-16 h-16 text-gray-300 mb-6" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">هنوز موردی خریداری نکرده‌اید</h2>
          <Link to="/" className="mt-6 text-purple-700 font-bold underline">
            بازگشت به صفحه اصلی و خرید 
          </Link>
        </div>
      )}
    </div>
  );
};

export default PurchasedPage;